/**
 * Token management service
 * Handles storing and retrieving JWT tokens in sessionStorage or localStorage
 * based on "Remember Me" preference
 */

const ACCESS_TOKEN_KEY = 'dentobook_access_token';
const REFRESH_TOKEN_KEY = 'dentobook_refresh_token';
const REDIRECT_URL_KEY = 'dentobook_redirect_url';
const REMEMBER_ME_KEY = 'dentobook_remember_me';

/**
 * Get the appropriate storage based on remember me preference
 */
const getStorage = (): Storage => {
    // Check if user has remember me enabled
    const rememberMe = localStorage.getItem(REMEMBER_ME_KEY) === 'true';
    return rememberMe ? localStorage : sessionStorage;
};

export const tokenService = {
    /**
     * Store access and refresh tokens
     * @param accessToken - JWT access token
     * @param refreshToken - JWT refresh token
     * @param rememberMe - Whether to persist tokens across browser sessions
     */
    setTokens(accessToken: string, refreshToken: string, rememberMe: boolean = false): void {
        // Store remember me preference
        if (rememberMe) {
            localStorage.setItem(REMEMBER_ME_KEY, 'true');
        } else {
            localStorage.removeItem(REMEMBER_ME_KEY);
        }

        const storage = getStorage();
        storage.setItem(ACCESS_TOKEN_KEY, accessToken);
        storage.setItem(REFRESH_TOKEN_KEY, refreshToken);
    },

    /**
     * Get access token from either storage
     */
    getAccessToken(): string | null {
        // Check both storages for backwards compatibility
        return localStorage.getItem(ACCESS_TOKEN_KEY) || sessionStorage.getItem(ACCESS_TOKEN_KEY);
    },

    /**
     * Get refresh token from either storage
     */
    getRefreshToken(): string | null {
        // Check both storages for backwards compatibility
        return localStorage.getItem(REFRESH_TOKEN_KEY) || sessionStorage.getItem(REFRESH_TOKEN_KEY);
    },

    /**
     * Clear all tokens and user data from both storages
     */
    clearTokens(): void {
        // Clear from both storages
        sessionStorage.removeItem(ACCESS_TOKEN_KEY);
        sessionStorage.removeItem(REFRESH_TOKEN_KEY);
        sessionStorage.removeItem(REDIRECT_URL_KEY);

        localStorage.removeItem(ACCESS_TOKEN_KEY);
        localStorage.removeItem(REFRESH_TOKEN_KEY);
        localStorage.removeItem(REMEMBER_ME_KEY);

        // Clear user-specific localStorage data
        localStorage.removeItem('dentobook-lang');
        // Optionally clear other user-specific data:
        // localStorage.removeItem('dismissedReminderDate');
        // localStorage.removeItem('modalShownCount');
    },

    /**
     * Check if user is authenticated (has access token)
     */
    isAuthenticated(): boolean {
        return !!this.getAccessToken();
    },

    /**
     * Store the URL user was trying to access before being redirected to login
     */
    setRedirectUrl(url: string): void {
        sessionStorage.setItem(REDIRECT_URL_KEY, url);
    },

    /**
     * Get and clear the redirect URL
     */
    getAndClearRedirectUrl(): string {
        const url = sessionStorage.getItem(REDIRECT_URL_KEY) || '/';
        sessionStorage.removeItem(REDIRECT_URL_KEY);
        return url;
    },

    /**
     * Decode JWT token and extract payload
     * @returns Decoded JWT payload or null if token is invalid
     */
    decodeToken(): any {
        const token = this.getAccessToken();
        if (!token) return null;

        try {
            const base64Url = token.split('.')[1];
            const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
            const jsonPayload = decodeURIComponent(
                atob(base64)
                    .split('')
                    .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
                    .join('')
            );
            return JSON.parse(jsonPayload);
        } catch (error) {
            return null;
        }
    },

    /**
     * Get organization ID from JWT token
     * @returns Organization ID from JWT claims or null
     */
    getOrganizationIdFromToken(): string | null {
        const payload = this.decodeToken();
        return payload?.organization_id || null;
    },

    /**
     * Get the membership role from JWT token (OrganizationMembership.role for the
     * currently selected organization). Prefer this over user.role for tenant
     * authorization checks; falls back to null for old tokens without the claim.
     * @returns membershipRole claim or null
     */
    getMembershipRole(): string | null {
        const payload = this.decodeToken();
        return payload?.membershipRole ?? null;
    },

    /**
     * Get the membership status from JWT token (OrganizationMembership.status for
     * the currently selected organization).
     * @returns membershipStatus claim or null
     */
    getMembershipStatus(): string | null {
        const payload = this.decodeToken();
        return payload?.membershipStatus ?? null;
    }
};
